import { useInjection } from 'inversify-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Collapse, Row, Spinner, Table } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { formatConcentration } from '../components/digiinsulin/formatConcentration';
import { IntensityChart, Peak } from '../components/digiinsulin/IntensityChart';
import { getStatusVariant, parseResponseData } from '../components/digiinsulin/utils';
import { API } from '../services/HTTP';

interface DigiInsulinAnalysisDetail {
    id: string;
    imageS3Key: string;
    originalFilename?: string;
    status: string;
    created_at: string;
    updated_at?: string;
    resultJson?: string;
    reportS3Key?: string;
}

interface UncertaintyDetail {
    std?: number | null;
    mean?: number | null;
    median?: number | null;
    ci95_low?: number | null;
    ci95_high?: number | null;
    cv_percent?: number | null;
    n_valid?: number | null;
}

interface DigiInsulinPrediction {
    test_line_label?: string | null;
    raw_signal?: number | null;
    normalized_signal?: number | null;
    predicted_concentration?: number | null;
    uncertainty?: UncertaintyDetail | number | null;
    alpha?: number | null;
    beta?: number | null;
    confidence?: string | null;
}

interface AnalysisFlag {
    passed: boolean;
    message?: string;
}

interface AnalysisStatusPayload {
    ok: boolean;
    flags?: Record<string, AnalysisFlag>;
}

interface DigiInsulinResultPayload {
    peaks?: Record<string, Peak>;
    summary?: unknown;
    diagnostics?: string;
    prediction?: DigiInsulinPrediction | null;
    analysisStatus?: AnalysisStatusPayload | null;
}

function parseResultPayload(resultJson?: string): DigiInsulinResultPayload | null {
    if (!resultJson) {
        return null;
    }

    try {
        return JSON.parse(resultJson) as DigiInsulinResultPayload;
    } catch (error) {
        console.error('Failed to parse DigiInsulin analysis result:', error);
        return null;
    }
}

function formatNumber(value?: number | null, digits = 2): string {
    if (value === undefined || value === null || !Number.isFinite(value)) {
        return '—';
    }
    return value.toFixed(digits);
}

function getConfidenceVariant(confidence?: string | null): string {
    switch (confidence?.toLowerCase()) {
        case 'ok':
        case 'high':
            return 'success';
        case 'extrapolated':
        case 'low':
            return 'warning';
        case 'failed':
        case 'invalid':
            return 'danger';
        default:
            return 'secondary';
    }
}

function prettifyFlagName(name: string): string {
    return name.replace(/_/g, ' ');
}

export function DigiInsulinResultDetailPage() {
    const { id } = useParams();
    const api: API = useInjection<API>('API');
    const [analysis, setAnalysis] = useState<DigiInsulinAnalysisDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [reportError, setReportError] = useState<string | null>(null);
    const [isDownloadingReport, setIsDownloadingReport] = useState(false);
    const [isReanalyzing, setIsReanalyzing] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loadAnalysis = useCallback(async () => {
        if (!id) {
            setAnalysis(null);
            setIsLoading(false);
            return null;
        }

        try {
            const response = await api.get(`digiinsulin/results/${encodeURIComponent(id)}`);
            const loaded = parseResponseData<DigiInsulinAnalysisDetail>(response.data);
            setAnalysis(loaded);
            setLoadError(null);
            return loaded;
        } catch (error) {
            console.error('Failed to load DigiInsulin analysis:', error);
            setAnalysis(null);
            setLoadError('Failed to load analysis. Please try again.');
            return null;
        }
    }, [api, id]);

    useEffect(() => {
        setIsLoading(true);
        loadAnalysis()
            .catch((error) => console.error(error))
            .finally(() => setIsLoading(false));
    }, [loadAnalysis]);

    // Poll while status is pending
    useEffect(() => {
        if (analysis?.status !== 'pending') {
            return;
        }

        pollTimerRef.current = setTimeout(async () => {
            await loadAnalysis();
        }, 5_000);

        return () => {
            if (pollTimerRef.current) {
                clearTimeout(pollTimerRef.current);
            }
        };
    }, [analysis?.status, analysis?.updated_at, loadAnalysis]);

    const handleReanalyze = useCallback(async () => {
        if (!id) {
            return;
        }

        setIsReanalyzing(true);
        setLoadError(null);

        try {
            const response = await api.post(`digiinsulin/reanalyze/${encodeURIComponent(id)}`, JSON.stringify({}));
            const updated = parseResponseData<DigiInsulinAnalysisDetail>(response.data);
            setAnalysis(updated);
        } catch (error) {
            console.error('Failed to re-analyze:', error);
            setLoadError('Failed to restart analysis. Please try again.');
        } finally {
            setIsReanalyzing(false);
        }
    }, [api, id]);

    const result = useMemo(() => parseResultPayload(analysis?.resultJson), [analysis?.resultJson]);
    const peaks = result?.peaks || {};
    const prediction = result?.prediction ?? null;
    const analysisStatus = result?.analysisStatus ?? null;
    const summaryValue = result?.summary ?? result;

    const handleDownloadReport = useCallback(async () => {
        if (!analysis?.id) {
            return;
        }

        setIsDownloadingReport(true);
        setReportError(null);

        try {
            const response = await api.get(`digiinsulin/report/${encodeURIComponent(analysis.id)}`);
            const payload = parseResponseData<{ reportUrl?: string }>(response.data);

            if (payload.reportUrl) {
                window.open(payload.reportUrl, '_blank', 'noreferrer');
            } else {
                setReportError('Failed to download PDF report. Please try again.');
            }
        } catch (error) {
            console.error('Failed to download DigiInsulin report:', error);
            setReportError('Failed to download PDF report. Please try again.');
        } finally {
            setIsDownloadingReport(false);
        }
    }, [analysis?.id, api]);

    if (isLoading) {
        return <Spinner animation="border" />;
    }

    if (loadError && !analysis) {
        return <Alert variant="danger">{loadError}</Alert>;
    }

    if (!analysis) {
        return <p className="text-muted">Analysis not found.</p>;
    }

    const isPending = analysis.status === 'pending';
    const canRetry = analysis.status === 'pending' || analysis.status === 'failed';

    return (
        <>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <div>
                    <div className="mb-2">
                        <Link to="/digiinsulin">← Back to results</Link>
                    </div>
                    <h1 className="mb-2">Analysis Detail</h1>
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <Badge bg={getStatusVariant(analysis.status)}>{analysis.status}</Badge>
                        <span className="text-muted">{new Date(analysis.created_at).toLocaleString()}</span>
                    </div>
                </div>
                <div className="d-flex gap-2">
                    {canRetry && (
                        <Button
                            variant={analysis.status === 'failed' ? 'outline-warning' : 'outline-secondary'}
                            onClick={handleReanalyze}
                            disabled={isReanalyzing}
                        >
                            {isReanalyzing ? <><Spinner animation="border" size="sm" /> Retrying…</> : '🔄 Retry Analysis'}
                        </Button>
                    )}
                </div>
            </div>

            {loadError && <Alert variant="danger" dismissible onClose={() => setLoadError(null)}>{loadError}</Alert>}
            {reportError && <Alert variant="danger">{reportError}</Alert>}

            {isPending ? (
                <Alert variant="info" className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    Analysis in progress — this page will update automatically.
                </Alert>
            ) : (
                <>
                    {/* ── Two prominent flashcard tiles ─────────────────── */}
                    <Row className="g-3 mb-4">
                        <Col xs={12} md={6}>
                            <Card className="h-100 text-center shadow-sm">
                                <Card.Header>Analysis Status</Card.Header>
                                <Card.Body className="d-flex flex-column align-items-center justify-content-center py-4">
                                    {analysisStatus ? (
                                        <>
                                            <div style={{ fontSize: '2.25rem', lineHeight: 1 }}>
                                                {analysisStatus.ok ? '✓' : '✗'}
                                            </div>
                                            <Badge
                                                bg={analysisStatus.ok ? 'success' : 'danger'}
                                                className="mt-2"
                                                style={{ fontSize: '1rem' }}
                                            >
                                                {analysisStatus.ok ? 'OK' : 'Failed'}
                                            </Badge>
                                        </>
                                    ) : (
                                        <span className="text-muted">Status unavailable</span>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} md={6}>
                            <Card className="h-100 text-center shadow-sm">
                                <Card.Header>Predicted Concentration</Card.Header>
                                <Card.Body className="d-flex flex-column align-items-center justify-content-center py-4">
                                    {prediction ? (
                                        <>
                                            <div style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1.1 }}>
                                                {formatConcentration(
                                                    prediction.predicted_concentration,
                                                    prediction.uncertainty,
                                                )}
                                            </div>
                                            {prediction.test_line_label && (
                                                <div className="text-muted mt-2" style={{ fontSize: '0.85rem' }}>
                                                    Test line: <strong>{prediction.test_line_label}</strong>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <span className="text-muted">No prediction available</span>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ── Expand / collapse toggle ───────────────────────── */}
                    <div className="mb-3">
                        <Button
                            variant="outline-secondary"
                            onClick={() => setDetailsOpen((open) => !open)}
                            aria-expanded={detailsOpen}
                            aria-controls="analysis-details-section"
                        >
                            {detailsOpen ? '▼ Hide details' : '▶ Show details'}
                        </Button>
                    </div>

                    {/* ── Collapsible details section ────────────────────── */}
                    <Collapse in={detailsOpen}>
                        <div id="analysis-details-section">
                            {analysisStatus?.flags && Object.keys(analysisStatus.flags).length > 0 && (
                                <Card className="mb-4">
                                    <Card.Header>Analysis Checks</Card.Header>
                                    <Card.Body>
                                        <Table size="sm" className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Check</th>
                                                    <th>Result</th>
                                                    <th>Details</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(analysisStatus.flags).map(([name, flag]) => (
                                                    <tr key={name}>
                                                        <td>{prettifyFlagName(name)}</td>
                                                        <td>
                                                            <Badge bg={flag.passed ? 'success' : 'danger'}>
                                                                {flag.passed ? 'pass' : 'fail'}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-muted">{flag.message || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>
                            )}

                            {prediction && (
                                <Card className="mb-4">
                                    <Card.Header>Prediction Signals</Card.Header>
                                    <Card.Body>
                                        <Table size="sm" className="mb-0">
                                            <tbody>
                                                <tr>
                                                    <th style={{ width: '40%' }}>Raw signal</th>
                                                    <td>{formatNumber(prediction.raw_signal)}</td>
                                                </tr>
                                                <tr>
                                                    <th>Normalized signal</th>
                                                    <td>{formatNumber(prediction.normalized_signal)}</td>
                                                </tr>
                                                {prediction.confidence && (
                                                    <tr>
                                                        <th>Confidence</th>
                                                        <td>
                                                            <Badge bg={getConfidenceVariant(prediction.confidence)}>
                                                                {prediction.confidence}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>
                            )}

                            {prediction?.uncertainty && typeof prediction.uncertainty === 'object' && (
                                <Card className="mb-4">
                                    <Card.Header>Prediction Uncertainty</Card.Header>
                                    <Card.Body>
                                        <Table size="sm" className="mb-0">
                                            <tbody>
                                                <tr>
                                                    <th style={{ width: '40%' }}>Mean</th>
                                                    <td>{formatNumber((prediction.uncertainty as UncertaintyDetail).mean)} IU/L</td>
                                                </tr>
                                                <tr>
                                                    <th>Median</th>
                                                    <td>{formatNumber((prediction.uncertainty as UncertaintyDetail).median)} IU/L</td>
                                                </tr>
                                                <tr>
                                                    <th>Std. deviation</th>
                                                    <td>{formatNumber((prediction.uncertainty as UncertaintyDetail).std)} IU/L</td>
                                                </tr>
                                                <tr>
                                                    <th>95% CI</th>
                                                    <td>
                                                        [{formatNumber((prediction.uncertainty as UncertaintyDetail).ci95_low)},{' '}
                                                        {formatNumber((prediction.uncertainty as UncertaintyDetail).ci95_high)}] IU/L
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th>CV</th>
                                                    <td>{formatNumber((prediction.uncertainty as UncertaintyDetail).cv_percent, 1)}%</td>
                                                </tr>
                                                <tr>
                                                    <th>Valid MC samples</th>
                                                    <td>{(prediction.uncertainty as UncertaintyDetail).n_valid ?? '—'}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>
                            )}

                            <Card className="mb-4">
                                <Card.Header>Peak Visualization</Card.Header>
                                <Card.Body>
                                    <IntensityChart peaks={peaks} />
                                </Card.Body>
                            </Card>

                            <Card>
                                <Card.Header>Summary</Card.Header>
                                <Card.Body>
                                    <pre
                                        className="mb-0"
                                        style={{
                                            fontSize: '0.85rem',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {summaryValue ? JSON.stringify(summaryValue, null, 2) : 'No summary available.'}
                                    </pre>
                                </Card.Body>
                            </Card>
                        </div>
                    </Collapse>
                </>
            )}
        </>
    );
}
